import type { ComponentType, ReactNode } from "react";
import { eventNames, shopWindowTypes, valueNames } from "@/modules/Constants";
import { isPixieOpen } from "@/modules/pixieWorkspace";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type ShopWindowConfigList from "@/plugin/ShopWindowConfigList";
import PixieIntroTip from "@/components/player/PixieIntroTip";
import { pixieIntentProps } from "@/components/shopwindow/ask/pixieChunk";
import type { ExperienceState } from "@/types/state";

// Analytics contract: this exact string is matched in
// appController.setShopWindow to emit the per-tab "Landscape <Tab> Tab
// Clicked" events, and Mixpanel dashboards filter Set Shop Window on
// pix_appregion="Landscape Nav". Renaming it silently breaks both.
const REGION = "Landscape Nav";

/** A config's icon: an SVG component, or text shown as is. */
type TabIcon = {
  name: string;
  value: ComponentType<{ className?: string }> | ReactNode;
};

type LandscapeNavProps = {
  experienceState: ExperienceState;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

// Landscape tab launcher. One round button per shop-window tab the
// experience actually has (Method / Ingredients / Essentials / Equipment / …,
// straight from SHOP_WINDOW_CONFIG_LIST — same source the desktop nav uses, so
// a button shows only when its tab exists). Tapping a button selects that tab
// (SET_SHOP_WINDOW), which in landscape also opens the overlay sheet
// (appController.setShopWindow → openShopWindow). No swipe-to-open — the
// buttons are the only open affordance.
export default function LandscapeNav({
  experienceState,
  onApplicationEvent,
}: LandscapeNavProps) {
  const applicationData = experienceState.applicationData;

  function onOpen(config: ShopWindowConfig) {
    // Pixie's tile toggles her Workspace, which leaves this rail on screen.
    if (
      config &&
      config.type === shopWindowTypes.ASK &&
      isPixieOpen(applicationData)
    ) {
      onApplicationEvent({
        region: REGION,
        name: eventNames.SHOP_WINDOW_EXIT,
        shopWindowConfig: config,
      });
      return;
    }
    if (onApplicationEvent && config) {
      onApplicationEvent({
        region: REGION,
        name: eventNames.SET_SHOP_WINDOW,
        shopWindowConfig: config,
      });
    }
  }

  const configs = applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG_LIST,
  ) as ShopWindowConfigList | undefined;

  if (!configs || !configs.count) {
    return null;
  }

  const ordered: ShopWindowConfig[] = [];
  const ask: ShopWindowConfig[] = [];
  for (let n = 0, max = configs.count; n < max; n++) {
    const config = configs.atIndex(n);
    if (config.type === shopWindowTypes.ASK) {
      ask.push(config);
    } else {
      ordered.push(config);
    }
  }
  const barConfigs = ordered.concat(ask);

  const pixieOpen = isPixieOpen(applicationData);
  const buttons = [];
  for (let n = 0; n < barConfigs.length; n++) {
    const config = barConfigs[n];
    const icon = config.icon as TabIcon;
    const IconComponent = icon.value;
    const iconClassNames = ["centered", `icon-${icon.name}-d`];
    const isAsk = config.type === shopWindowTypes.ASK;

    const button = (
      <button
        key={`landscape-tab-${config.name}`}
        type="button"
        className={`landscape-tab-btn hover${isAsk && pixieOpen ? " is-active" : ""}`}
        title={config.title}
        aria-label={config.title}
        onClick={() => onOpen(config)}
        {...(isAsk ? pixieIntentProps : null)}
      >
        {typeof IconComponent === "function" ? (
          <IconComponent className={iconClassNames.join(" ")} />
        ) : (
          <span className={iconClassNames.join(" ")}>{IconComponent}</span>
        )}
      </button>
    );
    buttons.push(
      isAsk ? (
        <div key="landscape-tab-pixie" className="landscape-tab-pixie tw:flex">
          {button}
          <PixieIntroTip
            placement="landscape"
            experienceState={experienceState}
            onApplicationEvent={onApplicationEvent}
          />
        </div>
      ) : (
        button
      ),
    );
  }

  return (
    <div className="landscape-nav" aria-label="Recipe tabs">
      {buttons}
    </div>
  );
}
